import axios from 'axios';

export const TOKEN_KEY = 'fatima_token';

/**
 * Axios instance — auth via `Authorization: Bearer <token>` header, token
 * stored in localStorage. No withCredentials/cookies: the frontend on
 * CloudFront and the API on API Gateway are different domains, so cookies
 * would be third-party and Chrome blocks them. Bearer avoids that entirely.
 * In dev, Vite proxies /api → localhost:3000/dev (serverless-offline).
 * In prod, VITE_API_URL points to the API Gateway invoke URL.
 */
const client = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api',
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-redirect to /login on an invalid/expired session.
// Three distinct sources return 401/403 here — only the first two mean
// "you're not logged in / token invalid":
//  1. API Gateway's Lambda authorizer DENY policy — body looks like
//     { message: "User is not authorized to access this resource" }
//     (no `success` key)
//  2. No Authorization header sent at all — API Gateway short-circuits to
//     401 { message: "Unauthorized" } without even invoking the authorizer.
// These should NOT redirect:
//  3. Our own Lambda handlers' forbidden() helper — body looks like
//     { success: false, error: 'Admin access required' }
//     (a logged-in user lacking a specific role)
//  4. API Gateway's "Missing Authentication Token" — despite the name, this
//     means the requested route/method doesn't exist in the deployed API
//     at all (e.g. a frontend deploy that shipped ahead of a backend
//     deploy adding a new endpoint). It has no `success` key either, so
//     without this check it looked identical to case 1 and force-logged-out
//     otherwise-valid sessions the moment they hit the missing route.
client.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const body   = err.response?.data;
    const isMissingRoute = status === 403 && body?.message === 'Missing Authentication Token';
    const isAuthDenial = !isMissingRoute
      && (status === 401 || (status === 403 && body && body.success === undefined));
    if (isAuthDenial && window.location.pathname !== '/login') {
      localStorage.removeItem(TOKEN_KEY);
      window.location.replace('/login');
    }
    return Promise.reject(err);
  }
);

export default client;
